import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, SvgIcon, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Header,
  StressArchetype,
  CortisolMetrics,
  StressSymptoms,
  ResourcesAndContentTabs,
  PersonalizedCart,
  RecommendationAndProgressTabs,
} from '../components/Results/StressInsights';
import { ReactComponent as LeftChevronIcon } from '../images/chevron-left.svg';
import { colors, theme } from '../utils/theme';
import { HormoneSnapshot, ResultType } from '../types';
import { useHormones } from '../config/api/queries';
import { MissingResults, RootineFooter } from '../components';
import { CHECKOUT_URLS, SHOPIFY_WEB_URL } from '../config';
import stressData from '../config/cortisol_types_v2.json';
import { RootineDivider } from '../atoms/RootineDivider';
import { ArticleContent } from '../components/Results/StressInsights/StressRelatedContent';

export type StressRecommendation = {
  title: string;
  description: string;
  secondary_description?: string;
  goal?: string;
  icon?: string;
  imgSrc?: string;
  link?: string;
};

export type StressRecommendationType = {
  name: string;
  label: string;
  description: string;
  imgSrc?: string;
  recs: StressRecommendation[];
};

export type StressData = {
  result: string;
  result_text: string;
  label: string;
  stress_archetype_percentage: string;
  color: string;
  what_does_this_mean: string;
  why_does_this_matter: string;
  symptoms: Array<{
    text: string;
    detail?: string;
    icon: string;
  }>;
  recommendations: StressRecommendationType[];
};

const relatedContent: ArticleContent[] = [
  {
    title: 'Understanding Your Rootine Stress Type',
    imgSrc: '/images/understanding-stress-type.jpeg',
    href: `${SHOPIFY_WEB_URL}/blogs/how-to-understand-your-stress-type`,
  },
  {
    title: 'Protein: The Key to Conquering Stress',
    imgSrc: '/images/protein-meal.jpeg',
    href: `${SHOPIFY_WEB_URL}/blogs/protein-the-key-to-conquering-stress`,
  },
  {
    title: 'Good Sleep Practices to Combat Stress',
    imgSrc: '/images/bed.jpeg',
    href: `${SHOPIFY_WEB_URL}/blogs/good-sleep-practices-to-combat-stress`,
  },
];

export const StressInsights = (): JSX.Element => {
  const navigate = useNavigate();
  const hormoneReport = useHormones();
  const [snapshots, setSnapshots] = useState<HormoneSnapshot[]>([]);
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const latestSnapshot = snapshots[0];

  useEffect(() => {
    if (hormoneReport.data && hormoneReport.data?.length) {
      const cortisolReport = hormoneReport.data.find(
        (report) => report.test_name === 'cortisol'
      );
      if (cortisolReport) {
        const sortedSnapshots = cortisolReport.snapshots.sort((a, b) => {
          return (
            new Date(b.sample_date).getTime() -
            new Date(a.sample_date).getTime()
          );
        });

        setSnapshots(sortedSnapshots);
      }
    }
  }, [hormoneReport.data]);

  const stressDataMap = stressData.reduce((acc, item) => {
    acc[item?.result as ResultType] = item;
    return acc;
  }, {} as Record<ResultType, StressData>);

  if (!snapshots.length) {
    return (
      <Box p={4}>
        <MissingResults
          head={'Missing Rootine Stress Insights'.toLocaleUpperCase()}
          subhead="Take an at-home lab test to inform your Rootine precision formula and gain access to your Cortisol insights."
          buttonLabel="Improve my Rootine"
          buttonLink={CHECKOUT_URLS.cortisol_test.link}
          foot="Back to home"
          footLink="/dashboard"
          height={80}
        />
        <RootineFooter />
      </Box>
    );
  }

  return (
    <Grid
      container
      alignItems="flex-start"
      flexDirection="column"
      sx={{ px: 2, mt: 6, [theme.breakpoints.down('sm')]: { mt: 8 } }}
    >
      <Button
        variant="text"
        startIcon={
          <SvgIcon
            component={LeftChevronIcon}
            inheritViewBox
            sx={{
              width: 18,
              height: 18,
              fill: colors.greys.BASE_WHITE,
              '&:hover': { fill: colors.greys.BG_WHITE },
            }}
          />
        }
        onClick={() => navigate(-1)}
        sx={{
          fontSize: 14,
          '&:hover': {
            background: 'transparent',
            color: colors.greys.TEXT_BLACK,
            textDecoration: 'underline',
          },
        }}
      >
        Back
      </Button>

      <Grid
        container
        flexDirection="column"
        sx={{ [theme.breakpoints.up('md')]: { px: 8 } }}
      >
        <Header hormoneSnapshots={snapshots} />

        {isMobile ? (
          // Mobile Layout
          <Grid container flexDirection="column">
            <Grid item sx={{ mt: 4 }}>
              <StressArchetype
                snapShot={latestSnapshot}
                stressDataMap={stressDataMap}
              />
            </Grid>

            <Grid item sx={{ mt: 4 }}>
              <CortisolMetrics snapshots={snapshots} />
            </Grid>

            <Grid item sx={{ mt: 4 }}>
              <StressSymptoms
                snapShot={latestSnapshot}
                stressDataMap={stressDataMap}
              />
            </Grid>
            <Grid item sx={{ mt: 4 }}>
              <RecommendationAndProgressTabs
                snapshots={snapshots}
                stressDataMap={stressDataMap}
              />
            </Grid>
            <Grid item sx={{ mt: 4 }}>
              <ResourcesAndContentTabs
                relatedContent={{
                  title: 'Stress Insights: Articles & Tips',
                  content: relatedContent,
                }}
              />
            </Grid>
            <Grid item sx={{ mt: 4 }}>
              <PersonalizedCart snapShot={latestSnapshot} />
            </Grid>
            <Grid item>
              <RootineFooter />
            </Grid>
          </Grid>
        ) : (
          // Desktop Layout
          <Grid container flexDirection="row" gap={6} sx={{ my: 4 }}>
            {/* Left Side */}
            <Grid container item flexDirection="column" mobile={12} tablet={4}>
              <Grid item>
                <StressArchetype
                  snapShot={latestSnapshot}
                  stressDataMap={stressDataMap}
                />
              </Grid>
              <Grid item sx={{ mt: 4 }}>
                <StressSymptoms
                  snapShot={latestSnapshot}
                  stressDataMap={stressDataMap}
                />
              </Grid>
              <Grid item sx={{ mt: 4 }}>
                <ResourcesAndContentTabs
                  relatedContent={{
                    title: 'Stress Insights: Articles & Tips',
                    content: relatedContent,
                  }}
                />
              </Grid>
              <Grid item sx={{ mt: 4 }}>
                <PersonalizedCart snapShot={latestSnapshot} />
              </Grid>
            </Grid>

            <RootineDivider
              dividerProps={{ orientation: 'vertical', flexItem: true }}
            />

            {/* Right Side */}
            <Grid container item flexDirection="column" mobile={12} tablet={6}>
              <CortisolMetrics snapshots={snapshots} />
              <Grid item sx={{ mt: 4 }}>
                <RecommendationAndProgressTabs
                  snapshots={snapshots}
                  stressDataMap={stressDataMap}
                />
              </Grid>
            </Grid>

            <Grid item>
              <RootineFooter />
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
